import { Component, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import * as THREE from "three";

const lerp = THREE.MathUtils.lerp;
const angDist = (a, b) => Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
const ELLIPSE = 1.44;
const WORLD_SCALE = [1.4, 1.35, 1.4];

const DECKS = [
  { tiers: 8, a0: 97, da: 9, y0: 13, dy: 5 },
  { tiers: 8, a0: 181, da: 8, y0: 61, dy: 5 },
];

function makeTurfTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 512;
  const g = c.getContext("2d");
  g.fillStyle = "#4a4526";
  g.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 9000; i++) {
    const v = Math.random();
    g.fillStyle =
      v > 0.7 ? "#5c552e" : v > 0.45 ? "#3e3a1f" : v > 0.25 ? "#6b5c34" : "#57512c";
    g.fillRect(Math.random() * 512, Math.random() * 512, 2, 2);
  }
  for (let i = 0; i < 8; i += 2) {
    g.fillStyle = "rgba(255,250,225,0.05)";
    g.fillRect(0, i * 64, 512, 64);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(6, 12);
  tex.anisotropy = 4;
  return tex;
}

function makeConcreteTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const g = c.getContext("2d");
  g.fillStyle = "#d8c39a";
  g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 4200; i++) {
    const v = Math.random();
    g.fillStyle = v > 0.6 ? "#e0cda6" : v > 0.3 ? "#c4ab84" : "#b59b77";
    g.fillRect(Math.random() * 256, Math.random() * 256, 2, 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(24, 2);
  return tex;
}

function makeRunwayTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 1024;
  const g = c.getContext("2d");
  g.fillStyle = "#2c2620";
  g.fillRect(0, 0, 256, 1024);
  const grad = g.createLinearGradient(0, 0, 256, 0);
  grad.addColorStop(0, "rgba(255,248,231,0)");
  grad.addColorStop(0.5, "rgba(255,248,231,0.14)");
  grad.addColorStop(1, "rgba(255,248,231,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 1024);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(1, 3);
  tex.anisotropy = 4;
  return tex;
}

function makeMonogramTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 512;
  const g = c.getContext("2d");
  g.clearRect(0, 0, 512, 512);
  g.strokeStyle = "#d4af37";
  g.lineWidth = 10;
  g.beginPath();
  g.arc(256, 256, 220, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 4;
  g.beginPath();
  g.arc(256, 256, 196, 0, Math.PI * 2);
  g.stroke();
  const gold = g.createLinearGradient(0, 120, 0, 400);
  gold.addColorStop(0, "#f3e5ab");
  gold.addColorStop(1, "#a87f24");
  g.fillStyle = gold;
  g.font = "700 280px Georgia, serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText("E", 256, 268);
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  return tex;
}

function makeWindowsTexture() {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 64;
  const g = c.getContext("2d");
  g.fillStyle = "#453d30";
  g.fillRect(0, 0, 1024, 64);
  for (let x = 12; x < 1024; x += 44) {
    const lit = Math.random() > 0.25;
    g.fillStyle = lit ? (Math.random() > 0.5 ? "#f3e5ab" : "#e8dcc8") : "#38312a";
    g.fillRect(x, 16, 26, 32);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.repeat.set(14, 1);
  return tex;
}

function makeScreenTexture() {
  const c = document.createElement("canvas");
  c.width = 2048;
  c.height = 1024;
  const g = c.getContext("2d");
  const bg = g.createRadialGradient(1024, 512, 120, 1024, 512, 1280);
  bg.addColorStop(0, "#5a4e3c");
  bg.addColorStop(0.6, "#4d4232");
  bg.addColorStop(1, "#38301f");
  g.fillStyle = bg;
  g.fillRect(0, 0, 2048, 1024);
  g.save();
  g.translate(1024, 512);
  for (let i = 0; i < 28; i++) {
    g.rotate((Math.PI * 2) / 28);
    g.fillStyle = i % 2 === 0 ? "rgba(212,175,55,0.05)" : "rgba(212,175,55,0.02)";
    g.beginPath();
    g.moveTo(0, 0);
    g.lineTo(-60, -1280);
    g.lineTo(60, -1280);
    g.closePath();
    g.fill();
  }
  g.restore();
  for (let i = 0; i < 90; i++) {
    const v = Math.random();
    g.fillStyle = v > 0.6 ? "rgba(253,246,216,0.6)" : "rgba(212,175,55,0.45)";
    const s = 2 + Math.random() * 4;
    g.fillRect(Math.random() * 2048, Math.random() * 1024, s, s);
  }
  g.strokeStyle = "#d4af37";
  g.lineWidth = 16;
  g.strokeRect(36, 36, 1976, 952);
  g.strokeStyle = "#f3e5ab";
  g.lineWidth = 4;
  g.strokeRect(76, 76, 1896, 872);
  [[36, 36], [2012, 36], [36, 988], [2012, 988]].forEach(([x, y]) => {
    g.save();
    g.translate(x, y);
    g.rotate(Math.PI / 4);
    g.fillStyle = "#d4af37";
    g.fillRect(-20, -20, 40, 40);
    g.restore();
  });
  g.save();
  g.translate(1024, 184);
  g.rotate(Math.PI / 4);
  g.fillStyle = "#d4af37";
  g.fillRect(-26, -26, 52, 52);
  g.fillStyle = "#fdf6d8";
  g.fillRect(-12, -12, 24, 24);
  g.restore();
  const gold = g.createLinearGradient(0, 260, 0, 640);
  gold.addColorStop(0, "#fdf6d8");
  gold.addColorStop(0.45, "#ffd968");
  gold.addColorStop(0.75, "#d4af37");
  gold.addColorStop(1, "#a87f24");
  g.textAlign = "center";
  let size = 450;
  g.font = `700 ${size}px Georgia, serif`;
  while (g.measureText("EMISON").width > 1700 && size > 200) {
    size -= 12;
    g.font = `700 ${size}px Georgia, serif`;
  }
  g.shadowColor = "rgba(0,0,0,0.6)";
  g.shadowBlur = 40;
  g.shadowOffsetY = 14;
  g.strokeStyle = "#332c20";
  g.lineWidth = 14;
  g.strokeText("EMISON", 1024, 536);
  g.fillStyle = gold;
  g.fillText("EMISON", 1024, 536);
  g.shadowColor = "transparent";
  g.shadowBlur = 0;
  g.shadowOffsetY = 0;
  g.strokeStyle = "rgba(253,246,216,0.7)";
  g.lineWidth = 5;
  g.strokeText("EMISON", 1024, 536);
  g.fillStyle = "#d4af37";
  g.fillRect(424, 644, 500, 6);
  g.fillRect(1124, 644, 500, 6);
  g.save();
  g.translate(1024, 648);
  g.rotate(Math.PI / 4);
  g.fillRect(-16, -16, 32, 32);
  g.restore();
  let sub = 184;
  g.font = `600 ${sub}px Georgia, serif`;
  while (g.measureText("M A N A G E M E N T").width > 1650 && sub > 80) {
    sub -= 8;
    g.font = `600 ${sub}px Georgia, serif`;
  }
  g.shadowColor = "rgba(0,0,0,0.45)";
  g.shadowBlur = 18;
  g.shadowOffsetY = 6;
  g.fillStyle = "#fff6dd";
  g.fillText("M A N A G E M E N T", 1024, 820);
  g.shadowColor = "transparent";
  g.shadowBlur = 0;
  g.shadowOffsetY = 0;
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 16;
  return tex;
}

function makeGlowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const g = c.getContext("2d");
  const glow = g.createRadialGradient(128, 128, 10, 128, 128, 128);
  glow.addColorStop(0, "rgba(255,217,104,0.9)");
  glow.addColorStop(0.5, "rgba(212,175,55,0.35)");
  glow.addColorStop(1, "rgba(212,175,55,0)");
  g.fillStyle = glow;
  g.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(c);
}

function makeShimmerTexture() {
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 64;
  const g = c.getContext("2d");
  const grad = g.createLinearGradient(0, 0, 512, 0);
  grad.addColorStop(0, "rgba(255,240,200,0)");
  grad.addColorStop(0.5, "rgba(255,242,205,0.9)");
  grad.addColorStop(1, "rgba(255,240,200,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 512, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.repeat.set(2, 1);
  return tex;
}

function makeRunwayLogoTexture() {
  const c = document.createElement("canvas");
  c.width = 2048;
  c.height = 512;
  const g = c.getContext("2d");
  g.clearRect(0, 0, 2048, 512);
  const gold = g.createLinearGradient(0, 60, 0, 460);
  gold.addColorStop(0, "#fdf6d8");
  gold.addColorStop(0.5, "#ffd968");
  gold.addColorStop(1, "#c9a227");
  g.textAlign = "center";
  g.shadowColor = "rgba(255,217,104,0.9)";
  g.shadowBlur = 34;
  g.fillStyle = gold;
  g.font = "700 150px Georgia, serif";
  g.fillText("E M I S O N", 1024, 220);
  g.font = "600 78px Georgia, serif";
  g.fillText("M A N A G E M E N T", 1024, 350);
  g.shadowBlur = 0;
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  return tex;
}

function makePanelTexture(bg, fg, text) {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 144;
  const g = c.getContext("2d");
  g.fillStyle = bg;
  g.fillRect(0, 0, 1024, 144);
  g.strokeStyle = fg;
  g.lineWidth = 3;
  g.strokeRect(10, 10, 1004, 124);
  g.fillStyle = fg;
  g.font = "600 56px Georgia, serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(text, 512, 74);
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  return tex;
}

function CameraRig({ progressRef }) {
  const intro = useRef(0);
  const mouseX = useRef(0);

  useEffect(() => {
    const onMove = (e) => {
      mouseX.current = (e.clientX / window.innerWidth) * 2 - 1;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  useFrame(({ camera, clock }, delta) => {
    intro.current = Math.min(1, intro.current + delta / 3.2);
    const ie = 1 - Math.pow(1 - intro.current, 3);
    const p = Math.min(1, ie * 0.07 + progressRef.current * 0.93);
    const t = clock.elapsedTime;
    const isMobile = window.innerWidth < 768;
    const fovT = isMobile ? 72 : 55;
    if (camera.fov !== fovT) {
      camera.fov = fovT;
      camera.updateProjectionMatrix();
    }
    const z = lerp(isMobile ? 390 : 360, isMobile ? -180 : -168, p);
    const y = lerp(isMobile ? 16 : 13, isMobile ? 40 : 30, p) + Math.sin(t * 0.5) * 0.6;
    camera.position.x += (mouseX.current * (isMobile ? 3 : 9) - camera.position.x) * 0.04;
    camera.position.y += (y - camera.position.y) * 0.07;
    camera.position.z += (z - camera.position.z) * 0.07;
    camera.lookAt(0, (12 + p * 46) * (isMobile ? 1.15 : 1), camera.position.z - 110);
  });
  return null;
}

const SEAT_COLORS = ["#c9b48f", "#a08b6d", "#8a7358", "#d4af37", "#e8dcc8", "#7a6648"];

function Seats() {
  const meshRef = useRef();
  useLayoutEffect(() => {
    const m = meshRef.current;
    if (!m) return;
    const dummy = new THREE.Object3D();
    const colors = SEAT_COLORS.map((c) => new THREE.Color(c));
    let i = 0;
    DECKS.forEach(({ tiers, a0, da, y0, dy }) => {
      for (let t = 0; t < tiers; t++) {
        const a = a0 + t * da;
        const b = a * ELLIPSE;
        const baseY = y0 + t * dy;
        const n = Math.floor(Math.PI * (a + b) / 1.7);
        for (let row = 0; row < 2; row++) {
          for (let s = 0; s < n; s++) {
            if (i >= 26000) break;
            const th = (s / n) * Math.PI * 2;
            if (angDist(th, Math.PI * 1.5) < 0.55) continue;
            if (angDist(th, Math.PI * 0.5) < 0.15) continue;
            const aisle = th % (Math.PI / 4);
            if (Math.min(aisle, Math.PI / 4 - aisle) < 0.012) continue;
            dummy.position.set(
              (a + row * 2.4) * Math.cos(th),
              baseY + row * 1.2,
              (b + row * 2.4 * ELLIPSE) * Math.sin(th)
            );
            dummy.rotation.y = -th + Math.PI / 2;
            dummy.updateMatrix();
            m.setMatrixAt(i, dummy.matrix);
            m.setColorAt(i, colors[Math.floor(Math.random() * colors.length)]);
            i++;
          }
        }
      }
    });
    m.count = i;
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }, []);
  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, 26000]}>
      <boxGeometry args={[1.15, 0.85, 1.0]} />
      <meshStandardMaterial roughness={1} />
    </instancedMesh>
  );
}

const SHIRTS = [
  "#f3e5ab", "#e8e4da", "#d8cdb8", "#c9b48f", "#fdfbf7",
  "#b09a7a", "#8c6d3f", "#565656",
  "#c94f4f", "#4f6dc9", "#4fa06b", "#9b59b6", "#e07b39", "#d4668c", "#3fa8a0", "#2d5aa0",
];
const SKINS = ["#8a6f52", "#6b5540", "#5a4632", "#a08560", "#7a5f45", "#4a3828"];

function Crowd() {
  const bodyRef = useRef();
  const headRef = useRef();
  const timeUniform = useMemo(() => ({ value: 0 }), []);
  const waveUniforms = useMemo(() => ({ phase: { value: 0 }, amp: { value: 0 } }), []);
  const crowdShader = useMemo(
    () => (shader) => {
      shader.uniforms.uCrowdTime = timeUniform;
      shader.uniforms.uWavePhase = waveUniforms.phase;
      shader.uniforms.uWaveAmp = waveUniforms.amp;
      shader.vertexShader =
        "uniform float uCrowdTime;\nuniform float uWavePhase;\nuniform float uWaveAmp;\n" +
        shader.vertexShader.replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
          float cSeed = instanceMatrix[3][0] * 12.9898 + instanceMatrix[3][2] * 78.233;
          float cHash = fract(sin(cSeed) * 43758.5453);
          float cBeat = sin(uCrowdTime * (2.0 + cHash * 2.4) + cHash * 6.2831);
          float cJump = max(cBeat, 0.0);
          transformed.y += cJump * cJump * (0.3 + cHash * 0.6);
          transformed.x += sin(uCrowdTime * 1.4 + cHash * 6.2831) * 0.08;
          transformed.z += cos(uCrowdTime * 1.1 + cHash * 6.2831) * 0.06;
          float cAngle = atan(instanceMatrix[3][2], instanceMatrix[3][0]);
          float cWave = pow(max(sin(cAngle - uWavePhase), 0.0), 6.0) * uWaveAmp;
          transformed.y += cWave * 2.4;`
        );
    },
    [timeUniform, waveUniforms]
  );
  useFrame(({ clock }) => {
    timeUniform.value = clock.elapsedTime;
    waveUniforms.phase.value = (clock.elapsedTime * 1.8) % (Math.PI * 2);
    const el = document.getElementById("products");
    if (el) {
      const r = el.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, -r.top / (r.height - window.innerHeight)));
      const target = p > 0.72 ? Math.min(1, (p - 0.72) / 0.12) : 0;
      waveUniforms.amp.value += (target - waveUniforms.amp.value) * 0.04;
    }
  });
  useLayoutEffect(() => {
    const bodies = bodyRef.current;
    const heads = headRef.current;
    if (!bodies || !heads) return;
    const dummy = new THREE.Object3D();
    let i = 0;
    DECKS.forEach(({ tiers, a0, da, y0, dy }) => {
      for (let t = 0; t < tiers; t++) {
        const a = a0 + t * da;
        const b = a * ELLIPSE;
        const baseY = y0 + t * dy;
        const n = Math.floor(Math.PI * (a + b) / 1.7);
        for (let row = 0; row < 2; row++) {
          for (let s = 0; s < n; s++) {
            if (i >= 20000) break;
            const th = (s / n) * Math.PI * 2;
            if (angDist(th, Math.PI * 1.5) < 0.55) continue;
            if (angDist(th, Math.PI * 0.5) < 0.15) continue;
            const aisle = th % (Math.PI / 4);
            if (Math.min(aisle, Math.PI / 4 - aisle) < 0.012) continue;
            if (Math.random() < 0.25) continue;
            const x = (a + row * 2.4) * Math.cos(th);
            const z = (b + row * 2.4 * ELLIPSE) * Math.sin(th);
            const y = baseY + row * 1.2;
            dummy.rotation.y = -th + Math.PI / 2;
            dummy.position.set(x, y + 0.95, z);
            dummy.updateMatrix();
            bodies.setMatrixAt(i, dummy.matrix);
            const r = Math.random();
            const idx =
              r < 0.28
                ? Math.floor(Math.random() * 5)
                : r < 0.46
                ? 5 + Math.floor(Math.random() * 3)
                : 8 + Math.floor(Math.random() * 8);
            bodies.setColorAt(i, new THREE.Color(SHIRTS[idx]));
            dummy.position.set(x, y + 1.78, z);
            dummy.updateMatrix();
            heads.setMatrixAt(i, dummy.matrix);
            heads.setColorAt(i, new THREE.Color(SKINS[Math.floor(Math.random() * SKINS.length)]));
            i++;
          }
        }
      }
    });
    bodies.count = i;
    heads.count = i;
    bodies.instanceMatrix.needsUpdate = true;
    heads.instanceMatrix.needsUpdate = true;
    if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
    if (heads.instanceColor) heads.instanceColor.needsUpdate = true;
  }, []);
  return (
    <>
      <instancedMesh ref={bodyRef} args={[undefined, undefined, 20000]}>
        <boxGeometry args={[0.72, 1.05, 0.55]} />
        <meshStandardMaterial roughness={1} onBeforeCompile={crowdShader} />
      </instancedMesh>
      <instancedMesh ref={headRef} args={[undefined, undefined, 20000]}>
        <sphereGeometry args={[0.3, 6, 6]} />
        <meshStandardMaterial roughness={1} onBeforeCompile={crowdShader} />
      </instancedMesh>
    </>
  );
}

function PhoneLights() {
  const matRef = useRef();
  const geo = useMemo(() => {
    const pts = [];
    const cols = [];
    const white = new THREE.Color("#fdfbf7");
    const gold = new THREE.Color("#ffd968");
    DECKS.forEach(({ tiers, a0, da, y0, dy }) => {
      for (let t = 0; t < tiers; t++) {
        const a = a0 + t * da;
        const b = a * ELLIPSE;
        const y = y0 + 2 + t * dy;
        const n = Math.floor(Math.PI * (a + b) / 5.5);
        for (let i = 0; i < n; i++) {
          const th = (i / n) * Math.PI * 2 + Math.random() * 0.04;
          if (angDist(th, Math.PI * 1.5) < 0.57) continue;
          if (angDist(th, Math.PI * 0.5) < 0.16) continue;
          pts.push(a * Math.cos(th), y + Math.random() * 3, b * Math.sin(th));
          const c = Math.random() > 0.5 ? white : gold;
          cols.push(c.r, c.g, c.b);
        }
      }
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(cols, 3));
    return g;
  }, []);
  useFrame(({ clock }) => {
    if (matRef.current) {
      matRef.current.opacity = 0.5 + Math.sin(clock.elapsedTime * 1.7) * 0.2;
    }
  });
  return (
    <points geometry={geo}>
      <pointsMaterial
        ref={matRef}
        size={2.6}
        vertexColors
        transparent
        opacity={0.6}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        sizeAttenuation
        toneMapped={false}
      />
    </points>
  );
}

function Tier({ a, y, concrete }) {
  return (
    <group>
      <mesh position={[0, y - 1.2, 0]} scale={[1, 1, ELLIPSE]}>
        <cylinderGeometry args={[a, a, 2.4, 96, 1, true]} />
        <meshStandardMaterial map={concrete} color="#f0e6d2" side={THREE.DoubleSide} roughness={1} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]} scale={[1, ELLIPSE, 1]}>
        <ringGeometry args={[a, a + 8.6, 96]} />
        <meshStandardMaterial map={concrete} color="#f7efdd" side={THREE.DoubleSide} roughness={1} />
      </mesh>
      <mesh position={[0, y + 0.35, 0]} scale={[1, 1, ELLIPSE]}>
        <cylinderGeometry args={[a + 0.2, a + 0.2, 0.45, 96, 1, true]} />
        <meshBasicMaterial color="#d4af37" transparent opacity={0.55} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>
    </group>
  );
}

function Terraces() {
  const concrete = useMemo(() => makeConcreteTexture(), []);
  const windows = useMemo(() => makeWindowsTexture(), []);
  const lower = useMemo(() => Array.from({ length: 8 }, (_, t) => ({ a: 96 + t * 9, y: 10.6 + t * 5 })), []);
  const upper = useMemo(() => Array.from({ length: 8 }, (_, t) => ({ a: 180 + t * 8, y: 58.6 + t * 5 })), []);
  return (
    <group>
      {lower.map(({ a, y }) => (
        <Tier key={`l-${a}`} a={a} y={y} concrete={concrete} />
      ))}
      {upper.map(({ a, y }) => (
        <Tier key={`u-${a}`} a={a} y={y} concrete={concrete} />
      ))}
      <mesh position={[0, 51.5, 0]} scale={[1, 1, ELLIPSE]}>
        <cylinderGeometry args={[172, 172, 7.5, 96, 1, true]} />
        <meshBasicMaterial map={windows} side={THREE.BackSide} toneMapped={false} />
      </mesh>
      {[47.4, 55.6].map((y) => (
        <mesh key={y} rotation={[Math.PI / 2, 0, 0]} position={[0, y, 0]} scale={[1, ELLIPSE, 1]}>
          <torusGeometry args={[172.5, 0.4, 8, 96]} />
          <meshBasicMaterial color="#d4af37" transparent opacity={0.8} toneMapped={false} />
        </mesh>
      ))}
      <mesh position={[0, 55, 0]} scale={[1, 1, ELLIPSE]}>
        <cylinderGeometry args={[244, 244, 96, 96, 1, true]} />
        <meshStandardMaterial map={concrete} color="#d8c39a" side={THREE.BackSide} roughness={1} />
      </mesh>
      <mesh position={[0, 99, 0]} scale={[1, 1, ELLIPSE]}>
        <cylinderGeometry args={[243, 243, 6, 96, 1, true]} />
        <meshBasicMaterial map={windows} side={THREE.BackSide} toneMapped={false} />
      </mesh>
      {[95.6, 102.4].map((y) => (
        <mesh key={y} rotation={[Math.PI / 2, 0, 0]} position={[0, y, 0]} scale={[1, ELLIPSE, 1]}>
          <torusGeometry args={[243.5, 0.35, 8, 96]} />
          <meshBasicMaterial color="#d4af37" transparent opacity={0.7} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function Roof() {
  const trusses = useMemo(
    () =>
      Array.from({ length: 32 }, (_, i) => {
        const th = (i / 32) * Math.PI * 2;
        const inner = new THREE.Vector3(202 * Math.cos(th), 106.6, 202 * ELLIPSE * Math.sin(th));
        const outer = new THREE.Vector3(320 * Math.cos(th), 112, 320 * ELLIPSE * Math.sin(th));
        const mid = inner.clone().add(outer).multiplyScalar(0.5);
        const len = inner.distanceTo(outer);
        const d = outer.clone().sub(inner).normalize();
        const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), d);
        return { mid, len, quat };
      }),
    []
  );
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 108.5, 0]} scale={[1, ELLIPSE, 1]}>
        <ringGeometry args={[200, 326, 96]} />
        <meshStandardMaterial color="#4a3a24" side={THREE.DoubleSide} roughness={1} />
      </mesh>
      {trusses.map((t, i) => (
        <mesh key={i} position={t.mid} quaternion={t.quat}>
          <boxGeometry args={[1.4, 1.4, t.len]} />
          <meshStandardMaterial color="#a08b6d" roughness={1} />
        </mesh>
      ))}
      {trusses.map((t, i) => (
        <mesh key={`glow-${i}`} position={[t.mid.x, t.mid.y - 0.95, t.mid.z]} quaternion={t.quat}>
          <boxGeometry args={[0.24, 0.24, t.len * 0.9]} />
          <meshBasicMaterial color="#f3e5ab" transparent opacity={0.6} toneMapped={false} />
        </mesh>
      ))}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 107.2, 0]} scale={[1, ELLIPSE, 1]}>
        <torusGeometry args={[200, 0.55, 8, 96]} />
        <meshBasicMaterial color="#fdf6d8" transparent opacity={0.95} toneMapped={false} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 110.4, 0]} scale={[1, ELLIPSE, 1]}>
        <torusGeometry args={[326, 1, 8, 96]} />
        <meshBasicMaterial color="#d4af37" transparent opacity={0.6} toneMapped={false} />
      </mesh>
    </group>
  );
}

function Chandelier() {
  const spinRef = useRef();
  const crystalRef = useRef();
  const RINGS = useMemo(
    () => [
      { r: 26, y: 74 },
      { r: 19, y: 68.5 },
      { r: 13, y: 63.5 },
      { r: 7.5, y: 59 },
    ],
    []
  );

  useLayoutEffect(() => {
    const m = crystalRef.current;
    if (!m) return;
    const dummy = new THREE.Object3D();
    const colors = ["#fdf6d8", "#ffd968", "#f3e5ab", "#ffffff"].map(
      (c) => new THREE.Color(c)
    );
    let i = 0;
    RINGS.forEach(({ r, y }) => {
      const n = Math.floor(r * 11);
      for (let s = 0; s < n; s++) {
        const th = (s / n) * Math.PI * 2;
        const strand = s % 3 === 0 ? 3 : s % 3 === 1 ? 2 : 1;
        for (let k = 0; k < strand; k++) {
          if (i >= 4200) break;
          dummy.position.set(r * Math.cos(th), y - 1.3 - k * 1.6, r * Math.sin(th));
          dummy.rotation.set(Math.random(), Math.random(), Math.random());
          dummy.updateMatrix();
          m.setMatrixAt(i, dummy.matrix);
          m.setColorAt(i, colors[Math.floor(Math.random() * colors.length)]);
          i++;
        }
      }
    });
    for (let k = 0; k < 10; k++) {
      if (i >= 4200) break;
      dummy.position.set(
        (Math.random() - 0.5) * 1.6,
        57 - k * 1.6,
        (Math.random() - 0.5) * 1.6
      );
      dummy.updateMatrix();
      m.setMatrixAt(i, dummy.matrix);
      m.setColorAt(i, colors[k % colors.length]);
      i++;
    }
    m.count = i;
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }, [RINGS]);

  const arms = useMemo(() => {
    const arr = [];
    RINGS.forEach(({ r, y }) => {
      for (let s = 0; s < 12; s++) {
        const th = (s / 12) * Math.PI * 2;
        const from = new THREE.Vector3(0, y + 2.4, 0);
        const to = new THREE.Vector3(r * Math.cos(th), y, r * Math.sin(th));
        const d = to.clone().sub(from);
        const len = d.length();
        const quat = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          d.normalize()
        );
        arr.push({ mid: from.clone().add(to).multiplyScalar(0.5), len, quat });
      }
    });
    return arr;
  }, [RINGS]);

  const cables = useMemo(() => {
    const top = new THREE.Vector3(0, 78, 0);
    return [
      new THREE.Vector3(0, 106, 280),
      new THREE.Vector3(0, 106, -300),
      new THREE.Vector3(200, 106, -10),
      new THREE.Vector3(-200, 106, -10),
    ].map((p) => {
      const d = p.clone().sub(top);
      const len = d.length();
      const quat = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        d.normalize()
      );
      const mid = top.clone().add(p).multiplyScalar(0.5);
      return { mid, len, quat };
    });
  }, []);

  useFrame((_, delta) => {
    if (spinRef.current) spinRef.current.rotation.y += delta * 0.06;
    if (crystalRef.current) {
      crystalRef.current.material.opacity = 0.85 + Math.sin(Date.now() / 350) * 0.12;
    }
  });

  return (
    <group position={[0, 0, 10]}>
      <group ref={spinRef}>
        <mesh position={[0, 68, 0]}>
          <cylinderGeometry args={[0.6, 0.9, 18, 8]} />
          <meshStandardMaterial color="#d4af37" metalness={0.9} roughness={0.2} />
        </mesh>
        <mesh position={[0, 77.4, 0]}>
          <sphereGeometry args={[1.4, 12, 12]} />
          <meshStandardMaterial color="#d4af37" metalness={0.9} roughness={0.15} />
        </mesh>
        <mesh position={[0, 50, 0]} scale={[1, 2.8, 1]}>
          <octahedronGeometry args={[1.4, 0]} />
          <meshBasicMaterial color="#fdf6d8" toneMapped={false} />
        </mesh>
        {RINGS.map(({ r, y }, ri) => (
          <group key={ri}>
            <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, y, 0]}>
              <torusGeometry args={[r, 0.65, 10, 64]} />
              <meshStandardMaterial color="#d4af37" metalness={0.85} roughness={0.25} />
            </mesh>
            {Array.from({ length: 14 }, (_, s) => {
              const th = (s / 14) * Math.PI * 2;
              return (
                <mesh key={s} position={[r * Math.cos(th), y + 1.15, r * Math.sin(th)]}>
                  <sphereGeometry args={[0.7, 8, 8]} />
                  <meshBasicMaterial color="#fff8e7" toneMapped={false} />
                </mesh>
              );
            })}
          </group>
        ))}
        {arms.map((a, i) => (
          <mesh key={i} position={a.mid} quaternion={a.quat}>
            <cylinderGeometry args={[0.2, 0.2, a.len, 6]} />
            <meshStandardMaterial color="#c9a227" metalness={0.85} roughness={0.3} />
          </mesh>
        ))}
        <instancedMesh ref={crystalRef} args={[undefined, undefined, 4200]}>
          <octahedronGeometry args={[0.45, 0]} />
          <meshBasicMaterial transparent opacity={0.9} toneMapped={false} />
        </instancedMesh>
      </group>
      {cables.map((cb, i) => (
        <mesh key={i} position={cb.mid} quaternion={cb.quat}>
          <cylinderGeometry args={[0.14, 0.14, cb.len, 4]} />
          <meshBasicMaterial color="#4d4232" />
        </mesh>
      ))}
      <pointLight position={[0, 66, 0]} color="#ffe9b8" intensity={16000} distance={480} decay={2} />
    </group>
  );
}

function RoofLights() {
  const coneGeo = useMemo(() => {
    const g = new THREE.ConeGeometry(34, 220, 20, 1, true);
    g.translate(0, -110, 0);
    return g;
  }, []);
  const heads = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => {
        const th = Math.PI / 8 + i * (Math.PI / 4);
        const x = 210 * Math.cos(th);
        const z = 210 * ELLIPSE * Math.sin(th);
        const head = new THREE.Vector3(x, 100, z);
        const target = new THREE.Vector3(x * 0.1, 0, z * 0.1);
        const dir = target.clone().sub(head).normalize();
        const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, -1, 0), dir);
        return { th, x, z, head, quat };
      }),
    []
  );
  return (
    <group>
      {heads.map((h, i) => (
        <group key={i}>
          <mesh position={[h.x, 101, h.z]} rotation={[0, -h.th + Math.PI / 2, 0]}>
            <boxGeometry args={[10, 3, 4.5]} />
            <meshStandardMaterial color="#8a7358" emissive="#fffaf0" emissiveIntensity={2.6} roughness={0.6} />
          </mesh>
          <mesh geometry={coneGeo} position={h.head} quaternion={h.quat}>
            <meshBasicMaterial
              color="#fff3d6"
              transparent
              opacity={0.035}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
          {i % 2 === 0 && (
            <pointLight
              position={[h.x * 0.8, 94, h.z * 0.8]}
              color="#fff3d6"
              intensity={45000}
              distance={1000}
              decay={2}
            />
          )}
        </group>
      ))}
    </group>
  );
}

function SweepBeams() {
  const ref = useRef();
  const coneGeo = useMemo(() => {
    const g = new THREE.ConeGeometry(13, 170, 16, 1, true);
    g.translate(0, -85, 0);
    return g;
  }, []);
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.16;
  });
  return (
    <group ref={ref}>
      {[0, 1, 2, 3].map((i) => {
        const th = (i / 4) * Math.PI * 2 + Math.PI / 4;
        const x = 160 * Math.cos(th);
        const z = 160 * ELLIPSE * Math.sin(th);
        const head = new THREE.Vector3(x, 98, z);
        const target = new THREE.Vector3(-x * 0.3, 18, -z * 0.3);
        const dir = target.clone().sub(head).normalize();
        const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, -1, 0), dir);
        return (
          <mesh key={i} geometry={coneGeo} position={head} quaternion={quat}>
            <meshBasicMaterial
              color="#ffe9b8"
              transparent
              opacity={0.05}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
        );
      })}
    </group>
  );
}

function PyroFountains() {
  const COUNT = 70;
  const datas = useMemo(
    () =>
      [
        [-40, -118],
        [40, -118],
      ].map(([x, z]) => {
        const pts = [];
        const vel = [];
        for (let i = 0; i < COUNT; i++) {
          pts.push(x + (Math.random() - 0.5) * 2.5, Math.random() * 32, z + (Math.random() - 0.5) * 2.5);
          vel.push(11 + Math.random() * 15);
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
        return { g, vel };
      }),
    []
  );
  useFrame((_, delta) => {
    datas.forEach((d) => {
      const pos = d.g.attributes.position;
      for (let i = 0; i < COUNT; i++) {
        let y = pos.getY(i) + d.vel[i] * delta;
        if (y > 34) y = 0;
        pos.setY(i, y);
      }
      pos.needsUpdate = true;
    });
  });
  return datas.map((d, i) => (
    <points key={i} geometry={d.g}>
      <pointsMaterial
        size={1.4}
        color="#ffd968"
        transparent
        opacity={0.9}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        toneMapped={false}
      />
    </points>
  ));
}

function makeRibbonTexture() {
  const APPROVALS = [
    { name: "CHOOSING INDEPENDENCE", amount: "$90K" },
    { name: "AIROS ENTERPRISES", amount: "$456K" },
    { name: "MARSHELL GROUP", amount: "$230K" },
    { name: "THE TAYLOR TEAM", amount: "$106K" },
    { name: "NETOPS SOLUTIONS", amount: "$1.1M" },
    { name: "BAY AREA TINTWORKS", amount: "$156K" },
    { name: "CREATIVE EMPLOYMENT", amount: "$70K" },
    { name: "ELITE JETZ", amount: "$105K" },
    { name: "NWBC GROUP", amount: "$115K" },
    { name: "WILLIAMS ARS", amount: "$100K" },
    { name: "WARE ES", amount: "$80K" },
  ];
  const COLORS = [
    { bg: "#d4af37", fg: "#4D4232" },
    { bg: "#fdfbf7", fg: "#6b4f1d" },
    { bg: "#e8dcc8", fg: "#4D4232" },
    { bg: "#5a4e3c", fg: "#e5c158" },
  ];
  const H = 260;
  const PAD = 110;
  const meas = document.createElement("canvas").getContext("2d");
  const widthOf = (text, font, ls) => {
    meas.font = font;
    if ("letterSpacing" in meas) meas.letterSpacing = ls;
    return meas.measureText(text).width;
  };
  const panelW = (a) =>
    Math.max(
      widthOf("◆ FUNDING APPROVED ◆", "600 26px Georgia, serif", "6px"),
      widthOf(a.name, "600 44px Georgia, serif", "5px"),
      widthOf(a.amount, "700 100px Georgia, serif", "2px")
    ) +
    PAD * 2;
  const cycleW = APPROVALS.reduce((w, a) => w + panelW(a), 0);
  const reps = Math.ceil(15000 / cycleW);
  const W = Math.round(cycleW * reps);
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const g = c.getContext("2d");
  g.textBaseline = "middle";
  let x = 0;
  for (let r = 0; r < reps; r++) {
    APPROVALS.forEach((a, i) => {
      const p = COLORS[i % COLORS.length];
      const w = panelW(a);
      g.fillStyle = p.bg;
      g.fillRect(x, 0, w, H);
      g.strokeStyle = p.fg;
      g.lineWidth = 3;
      g.strokeRect(x + 18, 18, w - 36, H - 36);
      g.fillStyle = p.fg;
      g.textAlign = "center";
      g.font = "600 26px Georgia, serif";
      if ("letterSpacing" in g) g.letterSpacing = "6px";
      g.globalAlpha = 0.75;
      g.fillText("◆ FUNDING APPROVED ◆", x + w / 2, 52);
      g.globalAlpha = 1;
      g.font = "600 44px Georgia, serif";
      if ("letterSpacing" in g) g.letterSpacing = "5px";
      g.fillText(a.name, x + w / 2, 110);
      g.font = "700 100px Georgia, serif";
      if ("letterSpacing" in g) g.letterSpacing = "2px";
      g.fillText(a.amount, x + w / 2, 190);
      x += w;
    });
  }
  g.fillStyle = "#d4af37";
  g.fillRect(0, 0, W, 8);
  g.fillRect(0, H - 8, W, 8);
  const out = document.createElement("canvas");
  out.width = W;
  out.height = H;
  const og = out.getContext("2d");
  og.translate(W, 0);
  og.scale(-1, 1);
  og.drawImage(c, 0, 0);
  const tex = new THREE.CanvasTexture(out);
  tex.wrapS = THREE.RepeatWrapping;
  tex.repeat.set(1, 1);
  tex.anisotropy = 16;
  return tex;
}

function PerimeterBoards() {
  const tex = useMemo(() => makeRibbonTexture(), []);
  useFrame((_, delta) => {
    tex.offset.x += delta * 0.008;
  });
  return (
    <group>
      <mesh position={[0, 5.1, 0]}>
        <cylinderGeometry args={[89.6, 89.6, 10, 128, 1, true]} />
        <meshStandardMaterial color="#38312a" roughness={1} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 4.7, 0]}>
        <cylinderGeometry args={[88, 88, 9, 128, 1, true]} />
        <meshBasicMaterial map={tex} side={THREE.BackSide} toneMapped={false} />
      </mesh>
      {[9.4, 0.15].map((y) => (
        <mesh key={y} rotation={[Math.PI / 2, 0, 0]} position={[0, y, 0]}>
          <torusGeometry args={[88.7, 0.22, 8, 128]} />
          <meshBasicMaterial color="#d4af37" transparent opacity={0.85} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function Field() {
  const turf = useMemo(() => makeTurfTexture(), []);
  const runway = useMemo(() => makeRunwayTexture(), []);
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 70]}>
        <planeGeometry args={[200, 460]} />
        <meshStandardMaterial map={turf} roughness={1} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.1, 70]}>
        <planeGeometry args={[8, 400]} />
        <meshStandardMaterial map={runway} roughness={0.25} metalness={0.6} />
      </mesh>
      {[-4.8, 4.8].map((x) => (
        <mesh key={x} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.13, 70]}>
          <planeGeometry args={[0.4, 400]} />
          <meshBasicMaterial color="#ffd968" transparent opacity={0.85} toneMapped={false} />
        </mesh>
      ))}
      {[-80, -40, 0, 40, 80].map((z) => (
        <mesh key={z} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.11, z]}>
          <planeGeometry args={[80, 0.5]} />
          <meshBasicMaterial color="#f3e5ab" transparent opacity={0.18} />
        </mesh>
      ))}
    </group>
  );
}

function Tunnel() {
  const frames = useMemo(() => Array.from({ length: 9 }, (_, i) => 152 + i * 11), []);
  const logoTex = useMemo(() => makeRunwayLogoTexture(), []);
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.14, 238]}>
        <planeGeometry args={[56, 14]} />
        <meshBasicMaterial map={logoTex} transparent opacity={0.9} toneMapped={false} />
      </mesh>
      {frames.map((z, i) => (
        <group key={i} position={[0, 0, z]}>
          <mesh position={[-15, 12, 0]}>
            <boxGeometry args={[2.5, 26, 2.5]} />
            <meshStandardMaterial color="#8a7358" roughness={1} />
          </mesh>
          <mesh position={[15, 12, 0]}>
            <boxGeometry args={[2.5, 26, 2.5]} />
            <meshStandardMaterial color="#8a7358" roughness={1} />
          </mesh>
          <mesh position={[0, 25.5, 0]}>
            <boxGeometry args={[32.5, 2.5, 2.5]} />
            <meshStandardMaterial color="#8a7358" roughness={1} />
          </mesh>
          <mesh position={[-13.4, 12, 0]}>
            <boxGeometry args={[0.18, 24, 0.18]} />
            <meshBasicMaterial color="#ffd968" transparent opacity={0.9} toneMapped={false} />
          </mesh>
          <mesh position={[13.4, 12, 0]}>
            <boxGeometry args={[0.18, 24, 0.18]} />
            <meshBasicMaterial color="#ffd968" transparent opacity={0.9} toneMapped={false} />
          </mesh>
        </group>
      ))}
      {[-12.5, 12.5].map((x) => (
        <mesh key={x} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.15, 196]}>
          <planeGeometry args={[0.4, 92]} />
          <meshBasicMaterial color="#ffd968" transparent opacity={0.55} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function Stage() {
  const screen = useRef();
  const fascia = useRef();
  const lift = useRef();
  const sway = useRef();
  const shimmer = useRef();
  const screenTex = useMemo(() => makeScreenTexture(), []);
  const glowTex = useMemo(() => makeGlowTexture(), []);
  const shimmerTex = useMemo(() => makeShimmerTexture(), []);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (screen.current) screen.current.material.opacity = 0.85 + Math.sin(t * 1.3) * 0.12;
    if (fascia.current) fascia.current.material.opacity = 0.45 + Math.sin(t * 2.1) * 0.18;
    const el = document.getElementById("products");
    if (el && lift.current) {
      const r = el.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, -r.top / (r.height - window.innerHeight)));
      const targetY = p > 0.78 ? 9 : 0.3;
      lift.current.position.y += (targetY - lift.current.position.y) * 0.045;
      shimmerTex.offset.x = (t * 0.1) % 1;
      if (shimmer.current) {
        const approach = Math.min(1, Math.max(0, (p - 0.45) / 0.35));
        shimmer.current.material.opacity += (approach * 0.5 - shimmer.current.material.opacity) * 0.06;
      }
    }
    if (sway.current) {
      sway.current.rotation.z = Math.sin(t * 1.6) * 0.05;
      sway.current.rotation.y = Math.sin(t * 0.9) * 0.25;
    }
  });
  return (
    <group position={[0, 0, -148]}>
      <mesh position={[0, 3, 0]}>
        <boxGeometry args={[104, 6, 38]} />
        <meshStandardMaterial color="#8a7358" roughness={1} />
      </mesh>
      <mesh ref={fascia} position={[0, 3.4, 19.3]}>
        <boxGeometry args={[104, 1.6, 0.5]} />
        <meshBasicMaterial color="#ffd968" transparent opacity={0.5} toneMapped={false} />
      </mesh>
      {[-54, 54].map((x) => (
        <group key={x} position={[x, 0, -15]}>
          <mesh position={[0, 41, 0]}>
            <boxGeometry args={[2.2, 70, 2.2]} />
            <meshStandardMaterial color="#4d4232" roughness={1} />
          </mesh>
          <mesh position={[x < 0 ? 1.3 : -1.3, 41, 0]}>
            <boxGeometry args={[0.3, 70, 0.3]} />
            <meshBasicMaterial color="#ffd968" toneMapped={false} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 48, -16.4]}>
        <planeGeometry args={[140, 66]} />
        <meshBasicMaterial
          map={glowTex}
          transparent
          opacity={0.55}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, 48, -15.6]}>
        <boxGeometry args={[110, 52, 2]} />
        <meshStandardMaterial color="#4d4232" roughness={0.9} />
      </mesh>
      <mesh ref={screen} position={[0, 48, -14.4]}>
        <planeGeometry args={[102, 46]} />
        <meshBasicMaterial map={screenTex} transparent opacity={0.95} toneMapped={false} fog={false} />
      </mesh>
      <mesh ref={shimmer} position={[0, 48, -14.2]} rotation={[0, 0, -0.32]}>
        <planeGeometry args={[130, 52]} />
        <meshBasicMaterial
          map={shimmerTex}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
          fog={false}
        />
      </mesh>
      {[-52, 52].map((x) => (
        <mesh key={x} position={[x, 12, -6]}>
          <boxGeometry args={[11, 24, 11]} />
          <meshStandardMaterial color="#4d4232" roughness={1} />
        </mesh>
      ))}
      {[-20, 0, 20].map((x) => (
        <mesh key={x} position={[x, 38, 4]} rotation={[0.5, 0, x * 0.012]}>
          <planeGeometry args={[7, 70]} />
          <meshBasicMaterial
            color="#fff3d6"
            transparent
            opacity={0.08}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      <group ref={lift} position={[-17, 0.3, 16]}>
        <mesh position={[0, 0.4, 0]}>
          <cylinderGeometry args={[6, 6.6, 0.8, 32]} />
          <meshStandardMaterial color="#4D4232" metalness={0.6} roughness={0.3} />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0.85, 0]}>
          <torusGeometry args={[6, 0.18, 8, 48]} />
          <meshBasicMaterial color="#ffd968" toneMapped={false} />
        </mesh>
        <group ref={sway} position={[0, 0.8, 0]}>
          {[-0.7, 0.7].map((x) => (
            <mesh key={x} position={[x, 1.6, 0]}>
              <cylinderGeometry args={[0.4, 0.5, 3.2, 8]} />
              <meshBasicMaterial color="#c9a227" toneMapped={false} />
            </mesh>
          ))}
          <mesh position={[0, 4.6, 0]}>
            <capsuleGeometry args={[1.1, 2.6, 6, 12]} />
            <meshBasicMaterial color="#d4af37" toneMapped={false} />
          </mesh>
          <mesh position={[0, 7.3, 0]}>
            <sphereGeometry args={[0.85, 12, 12]} />
            <meshBasicMaterial color="#f3e5ab" toneMapped={false} />
          </mesh>
          <mesh position={[1.5, 6.2, 0]} rotation={[0, 0, -0.9]}>
            <cylinderGeometry args={[0.28, 0.32, 2.8, 8]} />
            <meshBasicMaterial color="#d4af37" toneMapped={false} />
          </mesh>
          <mesh position={[2.7, 7.6, 0]}>
            <sphereGeometry args={[0.4, 8, 8]} />
            <meshBasicMaterial color="#fdf6d8" toneMapped={false} />
          </mesh>
          <mesh position={[-1.4, 4.6, 0]} rotation={[0, 0, 0.4]}>
            <cylinderGeometry args={[0.28, 0.32, 2.6, 8]} />
            <meshBasicMaterial color="#d4af37" toneMapped={false} />
          </mesh>
        </group>
        <mesh position={[0, 22, 0]}>
          <cylinderGeometry args={[1.2, 5.5, 30, 16, 1, true]} />
          <meshBasicMaterial
            color="#fff3d6"
            transparent
            opacity={0.07}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
        <pointLight position={[0, 10, 3]} color="#ffd968" intensity={2500} distance={70} decay={2} />
      </group>
    </group>
  );
}

function Dust() {
  const ref = useRef();
  const geo = useMemo(() => {
    const pts = [];
    for (let i = 0; i < 600; i++) {
      pts.push(
        (Math.random() - 0.5) * 400,
        Math.random() * 120,
        -220 + Math.random() * 560
      );
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return g;
  }, []);
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.012;
  });
  return (
    <points ref={ref} geometry={geo}>
      <pointsMaterial
        size={1.2}
        color="#fdf6d8"
        transparent
        opacity={0.4}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

function makeMarbleSealTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 1024;
  const g = c.getContext("2d");
  g.fillStyle = "#f4efe3";
  g.fillRect(0, 0, 1024, 1024);
  for (let i = 0; i < 22; i++) {
    const gx = Math.random() * 1024;
    const gy = Math.random() * 1024;
    const gr = g.createRadialGradient(gx, gy, 10, gx, gy, 120 + Math.random() * 160);
    gr.addColorStop(0, "rgba(200,185,150,0.12)");
    gr.addColorStop(1, "rgba(200,185,150,0)");
    g.fillStyle = gr;
    g.fillRect(0, 0, 1024, 1024);
  }
  for (let i = 0; i < 26; i++) {
    g.strokeStyle =
      i % 5 === 0
        ? `rgba(190,155,60,${0.08 + Math.random() * 0.08})`
        : `rgba(140,120,90,${0.07 + Math.random() * 0.08})`;
    g.lineWidth = 1 + Math.random() * 2;
    g.beginPath();
    let x = Math.random() * 1024;
    let y = Math.random() * 1024;
    g.moveTo(x, y);
    for (let s = 0; s < 8; s++) {
      x += (Math.random() - 0.5) * 260;
      y += (Math.random() - 0.5) * 260;
      g.lineTo(x, y);
    }
    g.stroke();
  }
  g.strokeStyle = "#c9a227";
  g.lineWidth = 16;
  g.beginPath();
  g.arc(512, 512, 478, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 5;
  g.beginPath();
  g.arc(512, 512, 438, 0, Math.PI * 2);
  g.stroke();
  const bronze = g.createLinearGradient(0, 220, 0, 820);
  bronze.addColorStop(0, "#4a3418");
  bronze.addColorStop(0.5, "#4D4232");
  bronze.addColorStop(1, "#4a3418");
  g.fillStyle = bronze;
  g.font = "700 560px Georgia, serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText("E", 512, 545);
  g.strokeStyle = "#c9a227";
  g.lineWidth = 7;
  g.strokeText("E", 512, 545);
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  return tex;
}

function CenterSeal() {
  const ring1 = useRef();
  const ring2 = useRef();
  const glow = useRef();
  const glowTex = useMemo(() => makeGlowTexture(), []);
  const marbleTex = useMemo(() => makeMarbleSealTexture(), []);
  useFrame(({ clock }, delta) => {
    if (ring1.current) ring1.current.rotation.z += delta * 0.3;
    if (ring2.current) ring2.current.rotation.z -= delta * 0.2;
    if (glow.current) {
      glow.current.material.opacity = 0.28 + Math.sin(clock.elapsedTime * 1.5) * 0.1;
    }
  });
  return (
    <group position={[0, 0, 10]}>
      <mesh position={[0, 0.35, 0]}>
        <cylinderGeometry args={[30.8, 31.8, 0.7, 64]} />
        <meshStandardMaterial color="#c9a227" metalness={0.75} roughness={0.3} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.72, 0]}>
        <circleGeometry args={[30.5, 64]} />
        <meshStandardMaterial map={marbleTex} roughness={0.28} metalness={0.08} />
      </mesh>
      <mesh ref={glow} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.15, 0]}>
        <circleGeometry args={[46, 48]} />
        <meshBasicMaterial
          map={glowTex}
          transparent
          opacity={0.5}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      <mesh ref={ring1} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.5, 0]}>
        <torusGeometry args={[33.5, 0.3, 8, 96]} />
        <meshBasicMaterial color="#ffd968" toneMapped={false} />
      </mesh>
      <mesh ref={ring2} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.45, 0]}>
        <torusGeometry args={[37.5, 0.2, 8, 96]} />
        <meshBasicMaterial color="#f3e5ab" transparent opacity={0.8} toneMapped={false} />
      </mesh>
      <mesh position={[0, 35, 0]}>
        <cylinderGeometry args={[2.2, 3.4, 70, 16, 1, true]} />
        <meshBasicMaterial
          color="#f3e5ab"
          transparent
          opacity={0.05}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

function SceneMood({ progressRef, ambientRef, hemiRef }) {
  const sky = useMemo(
    () => ({ a: new THREE.Color("#a68b54"), b: new THREE.Color("#cf8638") }),
    []
  );
  const amb = useMemo(
    () => ({ a: new THREE.Color("#f0e0b8"), b: new THREE.Color("#ffd9a0") }),
    []
  );
  const hemi = useMemo(
    () => ({ a: new THREE.Color("#fff6dd"), b: new THREE.Color("#ffce8a") }),
    []
  );
  const tmp = useMemo(() => new THREE.Color(), []);
  useFrame(({ scene }) => {
    const p = progressRef.current;
    tmp.copy(sky.a).lerp(sky.b, p);
    if (scene.background) scene.background.copy(tmp);
    if (scene.fog) scene.fog.color.copy(tmp);
    if (ambientRef.current) {
      ambientRef.current.color.copy(tmp.copy(amb.a).lerp(amb.b, p));
      ambientRef.current.intensity = 0.85 - p * 0.15;
    }
    if (hemiRef.current) {
      hemiRef.current.color.copy(tmp.copy(hemi.a).lerp(hemi.b, p));
    }
  });
  return null;
}

class GLBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 60%, rgba(212,175,55,0.25) 0%, #ffffff 70%)",
          }}
        />
      );
    }
    return this.props.children;
  }
}

const StadiumScene = ({ progressRef }) => {
  const ambientRef = useRef();
  const hemiRef = useRef();
  return (
    <GLBoundary>
      <Canvas
        data-testid="stadium-canvas"
        camera={{ fov: 55, near: 0.5, far: 2200, position: [0, 13, 360] }}
        dpr={[1, 1.75]}
        gl={{ antialias: true, powerPreference: "high-performance", toneMappingExposure: 1.2 }}
      >
        <color attach="background" args={["#a68b54"]} />
        <fog attach="fog" args={["#a68b54", 120, 950]} />
        <ambientLight ref={ambientRef} intensity={0.85} color="#f0e0b8" />
        <hemisphereLight ref={hemiRef} args={["#fff6dd", "#6b5326", 0.7]} />
        <CameraRig progressRef={progressRef} />
        <SceneMood progressRef={progressRef} ambientRef={ambientRef} hemiRef={hemiRef} />
        <group scale={WORLD_SCALE}>
          <Seats />
          <Crowd />
          <PhoneLights />
          <Terraces />
          <Roof />
          <Chandelier />
          <RoofLights />
          <SweepBeams />
          <CenterSeal />
          <PerimeterBoards />
          <Field />
          <Tunnel />
          <Stage />
          <Dust />
        </group>
        <EffectComposer>
          <Bloom intensity={0.5} luminanceThreshold={0.65} luminanceSmoothing={0.25} mipmapBlur radius={0.7} />
        </EffectComposer>
      </Canvas>
    </GLBoundary>
  );
};

export default StadiumScene;
